import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../config';
import { RootState } from '../store/store';
import { WhatsappPhone } from '../store/services/whatsappApi';
import { handleApiError } from '../utils/apiErrorHandler';

// Define interfaces for our API data
export interface CatalogCategory {
  id: number;
  name: string;
  description?: string;
  parent_id?: number;
  user_id: number;
  whatsapp_category_id?: string;
  created_at: string;
  updated_at?: string;
}

export interface MediaAssetInfo {
  id: number;
  file_name: string;
  file_type: string;
  mime_type: string;
  original_url?: string;
}

export interface CatalogItem {
  id: number;
  name: string;
  description?: string;
  price: number;
  currency: string;
  retailer_id?: string;
  url?: string;
  is_available: boolean;
  whatsapp_product_id?: string;
  whatsapp_phone_id: number;
  user_id: number;
  metadata?: any;
  created_at: string;
  updated_at?: string;
  categories: CatalogCategory[];
  images: MediaAssetInfo[];
}

export interface Order {
  id: number;
  catalog_item_id: number;
  customer_phone: string;
  customer_name?: string;
  quantity: number;
  total_price: number;
  currency: string;
  status: string;
  payment_method: string;
  payment_id?: string;
  user_id: number;
  whatsapp_phone_id: number;
  metadata?: any;
  created_at: string;
  updated_at?: string;
  catalog_item: CatalogItem;
}

export interface PaymentTransaction {
  id: number;
  order_id: number;
  transaction_id?: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  phone_number: string;
  checkout_request_id?: string;
  result_code?: string;
  result_desc?: string;
  metadata?: any;
  created_at: string;
  updated_at?: string;
}

export interface OrderWithPayments extends Order {
  payment_transactions: PaymentTransaction[];
}

export interface Catalog {
  id: string;
  name: string;
  whatsapp_phone_id: number;
  user_id: number;
  created_at: string;
  updated_at?: string;
}

// Create our API with endpoints
export const catalogApi = createApi({
  reducerPath: 'catalogApi',
  baseQuery: async (args, api, extraOptions) => {
    // Use fetchBaseQuery as the underlying query function
    const result = await fetchBaseQuery({
      baseUrl: API_BASE_URL,
      prepareHeaders: (headers, { getState }) => {
        // Get token from auth state
        const token = (getState() as RootState).auth.token;
        
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
        
        return headers;
      },
    })(args, api, extraOptions);
    
    // Handle errors like 403 Forbidden
    if (result.error) {
      handleApiError(result.error, typeof args === 'string' ? args : args.url);
    }
    
    return result;
  },
  tagTypes: [
    'CatalogCategories',
    'CatalogItems',
    'Orders',
    'MediaAssets',
    'Catalogs',
  ],
  endpoints: (builder) => ({
    // WhatsApp Phones (reused from whatsappApi)
    getWhatsappPhones: builder.query<WhatsappPhone[], void>({
      query: () => '/whatsapp/phones/',
    }),
    
    // Catalog Categories
    getCatalogCategories: builder.query<CatalogCategory[], void>({
      query: () => '/catalog/categories/',
      providesTags: ['CatalogCategories']
    }),
    
    getCatalogCategory: builder.query<CatalogCategory, number>({
      query: (id) => `/catalog/categories/${id}`,
      providesTags: (result, error, id) => [{ type: 'CatalogCategories', id }]
    }),
    
    createCatalogCategory: builder.mutation<CatalogCategory, Partial<CatalogCategory>>({
      query: (body) => ({
        url: '/catalog/categories/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CatalogCategories']
    }),
    
    updateCatalogCategory: builder.mutation<CatalogCategory, Partial<CatalogCategory> & { id: number }>({
      query: ({ id, ...body }) => ({
        url: `/catalog/categories/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CatalogCategories', id }]
    }),
    
    deleteCatalogCategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `/catalog/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CatalogCategories']
    }),
    
    // Catalog Items
    getCatalogItems: builder.query<CatalogItem[], { category_id?: number, whatsapp_phone_id?: number, is_available?: boolean }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.category_id) queryParams.append('category_id', params.category_id.toString());
        if (params.whatsapp_phone_id) queryParams.append('whatsapp_phone_id', params.whatsapp_phone_id.toString());
        if (params.is_available !== undefined) queryParams.append('is_available', params.is_available.toString());
        
        return `/catalog/items/?${queryParams.toString()}`;
      },
      providesTags: ['CatalogItems']
    }),
    
    getCatalogItem: builder.query<CatalogItem, number>({
      query: (id) => `/catalog/items/${id}`,
      providesTags: (result, error, id) => [{ type: 'CatalogItems', id }]
    }),
    
    createCatalogItem: builder.mutation<CatalogItem, Partial<CatalogItem>>({
      query: (body) => ({
        url: '/catalog/items/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CatalogItems']
    }),
    
    updateCatalogItem: builder.mutation<CatalogItem, Partial<CatalogItem> & { id: number }>({
      query: ({ id, ...body }) => ({
        url: `/catalog/items/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CatalogItems', id }]
    }),
    
    deleteCatalogItem: builder.mutation<void, number>({
      query: (id) => ({
        url: `/catalog/items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CatalogItems']
    }),
    
    syncCatalog: builder.mutation<{ message: string }, { whatsapp_phone_id: number; catalog_id?: string }>({
      query: ({ whatsapp_phone_id, catalog_id }) => ({
        url: `/catalog/items/sync?whatsapp_phone_id=${whatsapp_phone_id}${catalog_id ? `&catalog_id=${catalog_id}` : ''}`,
        method: 'POST',
      }),
    }),

    // Catalogs
    getCatalogs: builder.query<Catalog[], void>({
      query: () => '/catalog/items/catalogs',
      providesTags: ['Catalogs']
    }),

    createCatalog: builder.mutation<Catalog, { name: string; whatsapp_phone_id: number }>({
      query: (body) => ({
        url: '/catalog/items/catalogs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Catalogs']
    }),

    getCommerceSettings: builder.query<{ whatsapp_phone_id: number; phone_number: string; linked_catalog_id?: string; settings: { is_cart_enabled: boolean; is_catalog_visible: boolean } }, number>({
      query: (whatsapp_phone_id) => `/catalog/items/commerce-settings/${whatsapp_phone_id}`,
    }),

    // Available Catalogs from Meta
    getAvailableCatalogs: builder.query<{ whatsapp_phone_id: number; phone_number: string; linked_catalog_id?: string; available_catalogs: Array<{ id: string; name: string }>; count: number }, number>({
      query: (whatsapp_phone_id) => `/catalog/items/available-catalogs/${whatsapp_phone_id}`,
      providesTags: ['Catalogs']
    }),

    // Link Catalog to Phone
    linkCatalog: builder.mutation<{ message: string; whatsapp_phone_id: number; catalog_id: string; phone_number: string }, { whatsapp_phone_id: number; catalog_id: string }>({
      query: ({ whatsapp_phone_id, catalog_id }) => ({
        url: `/catalog/items/link-catalog/${whatsapp_phone_id}?catalog_id=${catalog_id}`,
        method: 'POST',
      }),
      invalidatesTags: ['Catalogs']
    }),
    
    // Orders
    getOrders: builder.query<Order[], { status?: string, whatsapp_phone_id?: number }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append('status', params.status);
        if (params.whatsapp_phone_id) queryParams.append('whatsapp_phone_id', params.whatsapp_phone_id.toString());
        
        return `/orders/?${queryParams.toString()}`;
      },
      providesTags: ['Orders']
    }),
    
    getOrder: builder.query<OrderWithPayments, number>({
      query: (id) => `/orders/${id}`,
      providesTags: (result, error, id) => [{ type: 'Orders', id }]
    }),
    
    createOrder: builder.mutation<Order, Partial<Order>>({
      query: (body) => ({
        url: '/orders/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Orders']
    }),
    
    updateOrder: builder.mutation<Order, Partial<Order> & { id: number }>({
      query: ({ id, ...body }) => ({
        url: `/orders/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Orders', id }]
    }),
    
    initiatePayment: builder.mutation<PaymentTransaction, number>({
      query: (orderId) => ({
        url: `/orders/${orderId}/payment`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Orders', id }]
    }),
    
    // Media Assets (reused from existing API)
    getMediaAssets: builder.query<MediaAssetInfo[], void>({
      query: () => '/media-assets',
      providesTags: ['MediaAssets']
    }),
    
    uploadMedia: builder.mutation<MediaAssetInfo, FormData>({
      query: (formData) => ({
        url: '/media-assets',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['MediaAssets']
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetWhatsappPhonesQuery,
  useGetCatalogCategoriesQuery,
  useGetCatalogCategoryQuery,
  useCreateCatalogCategoryMutation,
  useUpdateCatalogCategoryMutation,
  useDeleteCatalogCategoryMutation,
  useGetCatalogItemsQuery,
  useGetCatalogItemQuery,
  useCreateCatalogItemMutation,
  useUpdateCatalogItemMutation,
  useDeleteCatalogItemMutation,
  useSyncCatalogMutation,
  useGetCatalogsQuery,
  useCreateCatalogMutation,
  useGetCommerceSettingsQuery,
  useGetAvailableCatalogsQuery,
  useLinkCatalogMutation,
  useGetOrdersQuery,
  useGetOrderQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useInitiatePaymentMutation,
  useGetMediaAssetsQuery,
  useUploadMediaMutation,
} = catalogApi;
